import { DOLLAR_TBD } from "api-types/builders/salesConfig";
import type { DocOption, SalesImage } from "building-types/options/optionSet";
import { optionPrice } from "doc/options/optionSetTools";
import { isExteriorPaletteOptionSet } from "doc/options/paletteOptionSet";
import { PlanLibraryBadge } from "lib/components/builders/plan-library/PlanLibraryBadge";
import { PlanLibraryOptionValue } from "lib/components/builders/plan-library/PlanLibraryOptionValue";
import { planLibraryActions } from "lib/components/builders/plan-library/planLibraryReducer";
import PlanLibrarySalesImageTable from "lib/components/builders/plan-library/PlanLibrarySalesImageTable";
import { usePlanLibraryDispatch, usePlanLibrarySelector } from "lib/components/builders/plan-library/planLibraryStore";
import { getTbdPriceLabelOmitDollarPrefix, getTbdPriceLabel } from "lib/components/helpers/getOptionPriceDisplay";
import { notifySalesImageWarning } from "lib/options/notifySalesImageWarning";
import { SALES_IMAGE_UPLOAD_HINT } from "lib/options/salesImageValidation";
import uploadSalesImageToS3 from "lib/options/uploadSalesImageToS3";
import { notifyUploadError } from "lib/utils/notifyUploadError";
import { useState } from "react";
import Button from "ui/buttons/Button";
import Checkbox from "ui/forms/CheckboxOld";
import Input from "ui/forms/Input";
import Textarea from "ui/forms/Textarea";
import useAppContext from "ui/hooks/useAppContext";
import IconUpload from "ui/icons/IconUpload";
import styles from "./PlanLibraryModal.module.scss";
import PlanLibraryModalWrapper from "./PlanLibraryModalWrapper";

export default function PlanLibraryOptionModal() {
  const { setUserNotification } = useAppContext();
  const dispatch = usePlanLibraryDispatch();

  const {
    builder,
    onHandleDiscarding,
    overridesToDiscard,
    inheritedOptionSet,
    isExteriorPalette,
    inheritedOption,
    optionOverride,
    modalOptionSet,
    modalOption,
    planType,
    planId,
  } = usePlanLibrarySelector((state) => {
    const modalOptionSetId = state.modalOptionSet ? state.modalOptionSet.id : "";
    const modalOptionId = state.modalOption ? state.modalOption.id : "";

    const inheritedOptionSet = state.inheritedOptionSets[modalOptionSetId];
    const isExteriorPalette = isExteriorPaletteOptionSet(inheritedOptionSet);
    const optionSetOverride = state.overrides[modalOptionSetId];
    const inheritedOption = inheritedOptionSet.options.find((x) => x.id === modalOptionId);
    const optionOverride = optionSetOverride?.options?.[modalOptionId];

    return {
      builder: state.builder,
      onHandleDiscarding: state.onHandleDiscarding,
      overridesToDiscard: state.overridesToDiscard,
      inheritedOptionSet,
      isExteriorPalette,
      inheritedOption,
      optionOverride,
      modalOptionSet: state.modalOptionSet,
      modalOption: state.modalOption,
      planType: state.plans.planType,
      planId: state.planId,
    };
  });

  const customTbdPriceLabel = builder.salesConfig?.optionsDisplayConfig?.tbdPriceLabel;
  const tbd = getTbdPriceLabelOmitDollarPrefix(customTbdPriceLabel);

  const [option, setOption] = useState(modalOption);
  const [priceText, setPriceText] = useState(optionPrice(option).toString());

  if (!option || !inheritedOption) {
    return null;
  }

  const inheritedPrice = inheritedOption.priceTbd ? getTbdPriceLabel(customTbdPriceLabel) : `$${inheritedOption.price}`;

  function discardOverrideForOptionProp(key: keyof DocOption, oldOptionSet = modalOptionSet) {
    if (!inheritedOption || !option || !oldOptionSet) {
      return;
    }

    dispatch(planLibraryActions.discardOptionPropOverride({ oldOptionSet, optionId: option.id, key }));
    setUserNotification({
      message: `Override discarded in ${inheritedOption.name}`,
      messageType: "success",
    });
  }

  function onCommitPriceText() {
    if (!option) {
      return;
    }
    const updatedOption = { ...option };

    let text = priceText.trim().toLowerCase();
    if (text === "") {
      text = "0";
      updatedOption.price = 0;
    } else {
      const newPrice = parseInt(text);
      if (!isNaN(newPrice) && Number.isInteger(newPrice)) {
        updatedOption.price = newPrice;
        text = newPrice.toString();
      } else {
        text = optionPrice(option).toString(); // revert
      }
    }

    if (updatedOption.price !== option.price) {
      setOption(updatedOption);
    }
    if (priceText !== text) {
      setPriceText(text);
    }
  }

  if (!modalOptionSet) {
    return null;
  }

  return (
    <PlanLibraryModalWrapper
      title={overridesToDiscard ? "Discard Option Overrides" : "Edit Option Overrides"}
      isBaseOption={option.id === modalOptionSet.defaultValue}
      inheritedOptionSet={inheritedOptionSet}
      submitEnabled={!!overridesToDiscard || !!option.name}
      onSubmit={() => {
        if (overridesToDiscard) {
          onHandleDiscarding?.();
        } else {
          const options = modalOptionSet.options.map((x) => (x.id === option.id ? option : x));

          dispatch(
            planLibraryActions.overrideOptionSet({
              newOptionSet: { ...modalOptionSet, options },
              oldOptionSet: modalOptionSet,
            }),
          );
        }
        dispatch(planLibraryActions.setOverridesToDiscard(null));
        dispatch(planLibraryActions.enableOverrideModal(null));
      }}
    >
      <div className={styles.wrapper}>
        <div className={styles.fieldWrapper}>
          <div className={styles.labelWrapper}>
            <PlanLibraryOptionValue
              field="Name"
              isOverridden={optionOverride?.name !== undefined}
              inheritedValue={inheritedOption.name}
            >
              <label htmlFor="name">Sales Name</label>
            </PlanLibraryOptionValue>

            <span className={styles.optionSetBadgeWrapper}>
              <PlanLibraryBadge
                isOverridden={optionOverride?.name !== undefined}
                onClickBadge={() => {
                  if (!optionOverride) {
                    return;
                  }
                  dispatch(
                    planLibraryActions.setOverridesToDiscard({
                      overridesToDiscard: { options: { [option.id]: { name: optionOverride.name } } },
                      onHandleDiscarding: () => discardOverrideForOptionProp("name"),
                    }),
                  );
                }}
                singleField
              />
            </span>
          </div>
          <Input
            value={option.name}
            type="text"
            name="name"
            required
            onChange={(e) => setOption({ ...option, name: e.target.value })}
          />
        </div>

        <div className={styles.halfWidth}>
          <div className={styles.fieldWrapper}>
            <div className={styles.labelWrapper}>
              <PlanLibraryOptionValue
                field="Builder Option ID"
                isOverridden={optionOverride?.builderOptionCode !== undefined}
                inheritedValue={inheritedOption.builderOptionCode}
              >
                <label htmlFor="builderOptionCode">Builder Option ID</label>
              </PlanLibraryOptionValue>
              <span className={styles.optionSetBadgeWrapper}>
                <PlanLibraryBadge
                  singleField
                  onClickBadge={() => {
                    if (!optionOverride) {
                      return;
                    }
                    dispatch(
                      planLibraryActions.setOverridesToDiscard({
                        overridesToDiscard: {
                          options: { [option.id]: { builderOptionCode: optionOverride.builderOptionCode } },
                        },
                        onHandleDiscarding: () => discardOverrideForOptionProp("builderOptionCode"),
                      }),
                    );
                  }}
                  isOverridden={!!optionOverride?.builderOptionCode}
                />
              </span>
            </div>
            <Input
              value={option.builderOptionCode ?? undefined}
              name="builderOptionCode"
              type="text"
              onChange={(e) => setOption({ ...option, builderOptionCode: e.target.value })}
            />
          </div>

          <div className={styles.fieldWrapper}>
            <div className={styles.labelWrapper}>
              <PlanLibraryOptionValue
                field="Price"
                isOverridden={optionOverride?.price !== undefined}
                inheritedValue={inheritedPrice}
              >
                <label htmlFor="price">Price</label>
              </PlanLibraryOptionValue>
              <span className={styles.optionSetBadgeWrapper}>
                <PlanLibraryBadge
                  isOverridden={optionOverride?.price !== undefined}
                  onClickBadge={() => {
                    if (!optionOverride) {
                      return;
                    }
                    dispatch(
                      planLibraryActions.setOverridesToDiscard({
                        overridesToDiscard: { options: { [option.id]: { price: optionOverride.price } } },
                        onHandleDiscarding: () => discardOverrideForOptionProp("price"),
                      }),
                    );
                  }}
                  singleField
                />
              </span>
            </div>
            <Input
              type="text"
              name="price"
              value={priceText}
              placeholder="0"
              onChange={(e) => setPriceText(e.target.value)}
              onBlur={() => onCommitPriceText()}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onCommitPriceText();
                }
              }}
            />
          </div>

          <div className={styles.fieldWrapper}>
            <div className={styles.labelWrapper}>
              <div />
              <span className={styles.optionSetBadgeWrapper}>
                <PlanLibraryBadge
                  isOverridden={optionOverride?.priceTbd !== undefined}
                  onClickBadge={() => {
                    if (!optionOverride) {
                      return;
                    }
                    dispatch(
                      planLibraryActions.setOverridesToDiscard({
                        overridesToDiscard: { options: { [option.id]: { priceTbd: optionOverride.priceTbd } } },
                        onHandleDiscarding: () => discardOverrideForOptionProp("priceTbd"),
                      }),
                    );
                  }}
                  singleField
                />
              </span>
            </div>
            <div className={styles.checkboxWrapper}>
              <Checkbox
                checked={option.priceTbd}
                disabled={!!priceText && priceText !== "0" && priceText !== DOLLAR_TBD}
                label={
                  <PlanLibraryOptionValue
                    field="price is {DOLLAR_TBD}"
                    isOverridden={optionOverride?.priceTbd !== undefined}
                    inheritedValue={inheritedOption.priceTbd ? "Enabled" : "Disabled"}
                  >
                    <div title={`Will display ${DOLLAR_TBD} when price is 0`}>Price is {DOLLAR_TBD}</div>
                  </PlanLibraryOptionValue>
                }
                onChange={() => {
                  const priceTbd = !option.priceTbd;
                  setOption({ ...option, priceTbd });
                  if (priceTbd) {
                    setPriceText(tbd);
                  } else {
                    setPriceText(optionPrice(option).toString());
                  }
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.fieldWrapper}>
          <div className={styles.labelWrapper}>
            <PlanLibraryOptionValue
              field="Description"
              isOverridden={optionOverride?.description !== undefined}
              inheritedValue={inheritedOption.description}
            >
              <label htmlFor="description">Description</label>
            </PlanLibraryOptionValue>
            <span className={styles.optionSetBadgeWrapper}>
              <PlanLibraryBadge
                isOverridden={optionOverride?.description !== undefined}
                onClickBadge={() => {
                  if (!optionOverride) {
                    return;
                  }
                  dispatch(
                    planLibraryActions.setOverridesToDiscard({
                      overridesToDiscard: {
                        options: { [option.id]: { description: optionOverride.description } },
                      },
                      onHandleDiscarding: () => discardOverrideForOptionProp("description"),
                    }),
                  );
                }}
                singleField
              />
            </span>
          </div>
          <Textarea
            value={option.description ?? ""}
            name="description"
            onChange={(e) => setOption({ ...option, description: e.target.value })}
            maxLength={600}
          />
        </div>

        {!isExteriorPalette && inheritedOption.salesImages && option.salesImages && (
          <div style={{ marginTop: "2.5em" }}>
            <div className={styles.fieldWrapper}>
              <div className={styles.labelWrapper}>
                <PlanLibraryOptionValue
                  field="Sales Images"
                  isOverridden={optionOverride?.salesImages !== undefined}
                  inheritedValue={`${inheritedOption.salesImages.length} Sales Images`}
                >
                  <label htmlFor="salesImages">Sales Images ({option.salesImages.length})</label>
                </PlanLibraryOptionValue>

                <span className={styles.optionSetBadgeWrapper}>
                  <PlanLibraryBadge
                    isOverridden={optionOverride?.salesImages !== undefined}
                    onClickBadge={() => {
                      if (!optionOverride) {
                        return;
                      }
                      dispatch(
                        planLibraryActions.setOverridesToDiscard({
                          overridesToDiscard: {
                            options: { [option.id]: { salesImages: optionOverride.salesImages } },
                          },
                          onHandleDiscarding: () => discardOverrideForOptionProp("salesImages"),
                        }),
                      );
                    }}
                    singleField
                  />
                </span>
              </div>
            </div>

            {option.salesImages && option.salesImages.length > 0 && (
              <PlanLibrarySalesImageTable
                salesImages={option.salesImages}
                setSalesImages={(newImages: SalesImage[]) => {
                  setOption({ ...option, salesImages: newImages });
                }}
              />
            )}

            <div className={styles.upload}>
              <Button
                onClick={async (event) => {
                  if (!option.salesImages) {
                    return;
                  }

                  try {
                    const results = await uploadSalesImageToS3({
                      planType,
                      planId,
                      optionSetId: modalOptionSet.id,
                      optionId: option.id,
                    });
                    if (!results) {
                      throw new Error("No results from upload");
                    }

                    setOption({ ...option, salesImages: [...option.salesImages, results.salesImage] });
                    notifySalesImageWarning(results.salesImage, setUserNotification);
                  } catch (e) {
                    notifyUploadError(e, setUserNotification);
                  }
                  if (event) {
                    event.stopPropagation();
                  }
                }}
                btnStyle="secondary"
                btnSize="medium"
              >
                <IconUpload size={18} />
                <span>Upload</span>
              </Button>
              <div className={styles.uploadHint}>{SALES_IMAGE_UPLOAD_HINT}</div>
            </div>
          </div>
        )}
      </div>
    </PlanLibraryModalWrapper>
  );
}
